<script setup>
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import useCustomer from '../../../store/customer.store'
import CustomerUpdate from './Update.vue'
import CustomerCreate from './Create.vue'
import { callApi } from '../../../utils'
import Table from '../../shared/Table.vue'
import apiAddress from '../../../api/address.api'
import useAuth from '../../../store/auth.store'
import customerApi from '../../../api/customer.api'
const { t } = useI18n()

const {
  customerList,
  getCustomerList,
  loading,
  pagination,
  filterOptions,
} = useCustomer()

const auth = useAuth()

console.log('User permissions:', auth.user.value?.permissions)

const allowDelete = auth.user.value?.permissions['CUSTOMER']?.includes('DELETE') ?? false

const $q = useQuasar()

const columns = [
  { name: 'name', align: 'left', label: t('customer.name'), field: 'name' },
  { name: 'phone', align: 'left', label: t('customer.phone'), field: 'phone' },
  { name: 'addresses', align: 'left', label: t('customer.addresses'), field: 'addresses' },
  {
    name: 'lastPaid', align: 'left', label: t('customer.lastPaid'), field: v => {
      const lastPaid = v.addresses?.flatMap(addr => addr.devices.map(d => {
        if (addr.devices.length === 1) {
          return d.lastPaidYear && d.lastPaidMonth ? `${d.lastPaidYear}-${d.lastPaidMonth}` : null
        }
        return d.lastPaidYear && d.lastPaidMonth ? `${d.lastPaidYear}-${d.lastPaidMonth} (${d.apartment})` : null
      })).filter(Boolean)
      if (lastPaid && lastPaid.length > 0) {
        return lastPaid.join(', ')
      }
      return ''
    },

  },
  { name: 'carNumbers', align: 'left', label: t('customer.car_numbers'), field: 'carNumbers' },
]

function paginate(props) {
  callApi([getCustomerList], {
    ...filterOptions.value,
    page: parseInt(props.pagination.page),
    'page-size': parseInt(props.pagination.rowsPerPage),
  }, $q, loading)
}

function showUpdate(_, row) {
  $q.dialog({
    component: CustomerUpdate,
    componentProps: { id: row._id },
  })
}

function createCustomer() {
  $q.dialog({ component: CustomerCreate })
}

async function toggleDevicePermit(row, address, device) {
  $q.dialog({
    title: t('customer.change_permission_title'),
    message: t('customer.change_permission_text', { phone: row.phone, address: address.name }),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    try {
      await apiAddress.addPermit({
        addressId: address._id, phone: row.phone, devices: [
          { deviceOId: device._id, apartment: device.apartment ?? '', permitted: !device.permitted },
        ]
      })
      await getCustomerList(filterOptions.value)
    } catch (e) {
      console.error('Failed to toggle device permit', e)
    }
  })
}

async function removeAccess(row) {
  $q.dialog({
    title: t('customer.control.remove_access.title'),
    message: t('customer.control.remove_access.desc', { phone: row.phone }),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    try {
      for (const addr of row.addresses ?? []) {
        await apiAddress.removeAllPermits({ addressId: String(addr._id), phone: row.phone })
      }
      await getCustomerList(filterOptions.value)
    } catch (e) {
      console.error('Failed to remove customer permissions', e)
    }
  })
}

async function removeCustomer(row) {
  $q.dialog({
    title: t('customer.control.remove_customer.title'),
    message: t('customer.control.remove_customer.desc', { phone: row.phone }),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    try {
      await customerApi.remove(row._id)
      await getCustomerList(filterOptions.value)
    } catch (e) {
      console.error('Failed to remove customer', e)
    }
  })
}
</script>

<template lang='pug'>
.customer-list
  component(
    :is="Table"
    :title='t("menu.customers")'
    :rows='customerList'
    :loading='loading'
    :columns='columns'
    row-key='_id'
    v-model:pagination="pagination"
    @request="paginate"
    @row-click='showUpdate'
  )
    template(v-slot:header='props')
      q-th(v-for="(col, index) in props.cols" :key="col.name")
        div {{ col.label }}
      q-th
        div {{ t("customer.control.title") }}
        
    template(v-slot:body="props")
      q-td(v-for="col in props.cols" :key="col.name")
        div(v-if="col.name==='addresses'")
          div(v-for="addr in (props.row.addresses ?? [])" :key="String(addr._id)" class="q-mb-xs")
            .text-caption.text-weight-medium.text-grey-7 {{ addr.name }}
            .row.items-center.flex-wrap(style="gap: 2px 8px;")
              q-checkbox(
                v-for="device in (addr.devices ?? [])"
                :key="String(device.deviceOId ?? device._id)"
                dense
                size="xs"
                :model-value="device.permitted"
                :label="(device.name || String(device.deviceOId)) + (device.apartment ? ` (${t('addresses.permit_dialog.apartmentNumber', { number: device.apartment })})` : '')"
                :disable="loading"
                @click.stop
                @update:model-value="toggleDevicePermit(props.row, addr, device)"
              )
        div(v-else-if="col.name==='carNumbers'")
          q-chip(
            v-for="(carNumber, index) in col.value"
            :key="index"
            color="blue"
            text-color="white"
            style="margin-right: 12px;"
          ) {{ carNumber }}
        div(v-else @click="showUpdate(props, props.row)") 
          | {{ col.value }}
      q-td
        q-btn(
          size='sm'
          icon='cancel'
          color='red'
          push
          round
          tooltip="$t('customer.control.remove_access.title')"
          @click.stop='removeAccess(props.row)'
          class="q-mr-sm"
        )
        q-btn(
          v-if="allowDelete"
          size='sm'
          icon='delete'
          color='red'
          push
          round
          tooltip="$t('customer.control.remove_customer.title')"
          @click.stop='removeCustomer(props.row)'
          class="q-mr-sm"
        )
    template(v-slot:no-data)
      .full-width.row.flex-center.q-py-xl
        span.text-h6.text-grey {{ t("table.nodata") }}

</template>

<style scoped>
.customer-list {
  height: 100%;
  min-height: 0;
}

:deep(.customer-table .q-table__container) {
  height: 70vh;
  /* or any fixed/max height you want */
}

:deep(.customer-table .q-table__middle) {
  max-height: 70vh;
  overflow: auto;
}

:deep(.customer-table thead tr th) {
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
}

:deep(.customer-table thead tr th:last-child) {
  z-index: 3;
}
</style>